import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { PROMO_HEADLINE, PROMO_BLURB } from '@/lib/promo';

interface PromoBannerProps {
  /** Compact single-line version for dashboards and billing pages */
  compact?: boolean;
  className?: string;
}

export function PromoBanner({ compact = false, className = '' }: PromoBannerProps) {
  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 rounded-xl border border-accent/30 bg-accent/10 px-4 py-3 ${className}`}
      >
        <Sparkles className="h-5 w-5 shrink-0 text-accent" />
        <p className="text-sm text-foreground">
          <span className="font-semibold">{PROMO_HEADLINE}.</span>{' '}
          <span className="text-muted-foreground">{PROMO_BLURB}</span>
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-primary via-[hsl(213,60%,22%)] to-[hsl(213,55%,28%)] p-8 md:p-12 text-center ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-accent/10 to-transparent" />
      <div className="relative z-10 mx-auto max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-accent-foreground shadow-lg shadow-accent/30">
          <Sparkles className="h-3 w-3" />
          Launch offer
        </span>
        <h3 className="mt-5 text-3xl md:text-4xl font-bold text-white">
          {PROMO_HEADLINE}
        </h3>
        <p className="mt-4 text-white/80">{PROMO_BLURB}</p>
        <ul className="mt-6 grid gap-2 text-left text-sm text-white/90 sm:grid-cols-2">
          {[
            'Your selected subjects from our catalogue',
            'Full personalised study plan',
            'Generous AI tutoring with Dev — 3,000 credits a month',
            'Full progress tracking and reports',
            'Parent dashboard and weekly reports',
            'Dinner table conversations',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}
